import clsx from "clsx";
import FocusTrap from "focus-trap-react";
import { FunctionComponent, useRef, useState } from "react";
import { Transition } from "react-transition-group";

import { SELECTORS } from "@ui/config";
import { withGracefulErrorBoundary } from "@ui/errors/GracefulErrorBoundary";
import { sharedStyles } from "@ui/shared-styles";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import {
  useLoopNavContext,
  LoopNavComponentProps,
} from "@voxmedia/duet-context/context/loop-nav-context";

import LoopNavCTA from "./components/LoopNavCTA";
import LoopNavMenu from "./components/LoopNavMenu";
import * as styles from "./LoopNav.css";
import { TRANSITION_TIME } from "./LoopNav.css";

const LoopNav: FunctionComponent<LoopNavComponentProps> = ({
  forceOpenLoopNav,
}) => {
  const { drawerId, isDrawerOpen, closeDrawer, openDrawer, loopNavData } =
    useLoopNavContext();

  // In contrast to `open`, `visible` only changes state after the transition.
  const [visible, setVisible] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const analytics = useAnalytics();

  if (!loopNavData) {
    return null;
  }

  const { sections, title } = loopNavData;

  const subTitleSection = sections.find((s) => s.current) || null;
  const subTitle = subTitleSection?.text;

  const toggleLoopNav = () => {
    if (!isDrawerOpen) {
      analytics?.emit({
        event: "loopnav",
        loopnav: {
          action: "click_dropdown",
          loopnav_name: title,
        },
      });
      openDrawer();
    } else {
      closeDrawer();
    }
  };

  return (
    <div className={clsx(SELECTORS.LoopNav, styles.container)}>
      <Transition
        in={forceOpenLoopNav || isDrawerOpen}
        timeout={TRANSITION_TIME}
        nodeRef={dialogRef}
        onEntered={() => setVisible(true)}
        onExited={() => setVisible(false)}
      >
        {(transitionStatus) => (
          <>
            <LoopNavCTA
              open={forceOpenLoopNav || isDrawerOpen}
              drawerId={drawerId}
              onClick={toggleLoopNav}
              title={title}
              subTitle={subTitle}
              transitionStatus={transitionStatus}
            />
            <div id="animate-under-me" className={styles.animationHelper} />
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <div
              id={drawerId}
              ref={dialogRef}
              className={clsx(
                styles.dialog,
                transitionStatus === "exited" && sharedStyles.layout.hide,
              )}
              onClick={(e) => {
                if (e.currentTarget === e.target) {
                  closeDrawer();
                }
              }}
              onKeyUp={(e) => {
                if (e.key === "Escape") {
                  closeDrawer();
                }
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="loopnav-title"
            >
              <section
                className={clsx(
                  styles.drawer,
                  styles.transformTransition,
                  styles.transitionDelay,
                  transitionStatus === "entering" && styles.loopNavOutside,
                  transitionStatus === "entered" && styles.loopNavInside,
                  transitionStatus === "exiting" && styles.loopNavOutside,
                )}
              >
                <FocusTrap
                  active={visible}
                  focusTrapOptions={{
                    escapeDeactivates: false,
                    clickOutsideDeactivates: false,
                    allowOutsideClick: true,
                  }}
                >
                  <div className={styles.focusTrapInnerWrapper}>
                    <LoopNavMenu
                      transitionStatus={transitionStatus}
                      sections={sections}
                      initialOpenSectionTitle={subTitle}
                      loopNavTitle={title}
                    />
                  </div>
                </FocusTrap>
              </section>
            </div>
          </>
        )}
      </Transition>
    </div>
  );
};

export default withGracefulErrorBoundary(LoopNav, {
  context: "LoopNavErrorBoundary",
});
